import { createModel } from '@rematch/core';

// Types
import type { RootModel } from './index';

import http from '../http/http-common';
import { generatePath } from 'react-router-dom';
import URL from '../../constants/api-constants';
import { toast } from 'react-toastify';

export type CalibrationCertificate = {
  id: string;
  file: string;
  startAt: string;
  dueAt: string;
  created_at: string;
  updated_at: string;
  breadcrumb: string;
};

const DEFAULT_STATE: {
  calibrationCertificates: CalibrationCertificate[] | null;
  calibrationCertificate: CalibrationCertificate | null;
} = {
  calibrationCertificates: null,
  calibrationCertificate: null,
};

export const calibration_certificates = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    setOne(state, payload) {
      return {
        ...state,
        calibrationCertificate: payload,
      };
    },
    setList(state, payload) {
      return {
        ...state,
        calibrationCertificates: payload,
      };
    },
    resetOne(state) {
      return {
        ...state,
        calibrationCertificate: DEFAULT_STATE.calibrationCertificate,
      };
    },
    resetList(state) {
      return {
        ...state,
        calibrationCertificates: DEFAULT_STATE.calibrationCertificates,
      };
    },
    resetState() {
      return DEFAULT_STATE;
    },
  },
  effects: (dispatch) => {
    return {
      async getCalibrationCertificate(id: string) {
        try {
          const response = await http.get(generatePath(URL.CONTROLLER_CALIBRATION_CERTIFICATE, { id }));

          if (response.status === 200) {
            dispatch.calibration_certificates.setOne(response.data);
          }
        } catch (error) {
          toast.error(error.message);
        }
      },

      async getCalibrationCertificates(controllerId: string) {
        try {
          const response = await http.get(URL.CONTROLLER_CALIBRATION_CERTIFICATES, {
            params: { controllerId },
          });
          if (response.status === 200) {
            dispatch.calibration_certificates.setList(response.data);
          }
        } catch (error) {
          toast.error(error.message);
        }
      },

      async createCalibrationCertificate({ formData }: { formData: FormData }) {
        try {
          const response = await http.post(generatePath(URL.CONTROLLER_CALIBRATION_CERTIFICATES), formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          if (response.status === 200) {
            toast.success('Certificate uploaded successfully');
          }
        } catch (error: any) {
          toast.error(error.message || 'Failed to upload certificate');
          throw error;
        }
      },

      async updateCalibrationCertificate({ id, body }: { id: string; body: { startAt: string; dueAt: string } }) {
        try {
          const response = await http.put(generatePath(URL.CONTROLLER_CALIBRATION_CERTIFICATES_EDIT, { id }), body);

          if (response.status === 200) {
            toast.success('Certificate updated successfully');
            dispatch.calibration_certificates.setOne(response.data);
          }
        } catch (error: any) {
          toast.error(error.message || 'Failed to update certificate');
          throw error;
        }
      },

      async deleteCalibrationCertificate({ id }: { id: string }) {
        try {
          const response = await http.delete(generatePath(URL.CONTROLLER_CALIBRATION_CERTIFICATES_DELETE, { id }));

          if (response.status === 200) {
            toast.success('Certificate deleted successfully');
            dispatch.calibration_certificates.setList(response.data);
          }
        } catch (error: any) {
          toast.error(error.message || 'Failed to delete certificate');
          throw error;
        }
      },
    };
  },
});
